import React, { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Loader2Icon } from "lucide-react";

interface NewSentinelFormProps {
  loading: boolean;
  onSubmit: (formData: { expectation: string; condition: string; qa?: Array<{ question: string; answer: string }> }) => void;
  onClose: () => void;
  qaItems?: Array<{ question: string; answer: string }>;
  setQaItems: React.Dispatch<React.SetStateAction<Array<{ question: string; answer: string }>>>;
  hasSubmitted?: boolean;
}

const NewSentinelForm: React.FC<NewSentinelFormProps> = ({
  loading,
  onSubmit,
  onClose,
  qaItems,
  setQaItems,
  hasSubmitted = false,
}) => {
  const [expectation, setExpectation] = useState("");
  const [condition, setCondition] = useState("");

  const handleSubmit = () => {
    onSubmit({
      expectation,
      condition,
      qa: qaItems || []
    });
  };

  const isFormValid = expectation.trim() !== "" && condition.trim() !== "";

  return (
    <div className="space-y-8">
      {/* Expectation Field */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-gc-med text-foreground">
            Expectation <span className="text-red-500">*</span>
          </Label>
        </div>
        <Textarea
          value={expectation}
          onChange={(e) => setExpectation(e.target.value)}
          placeholder="The model should never offer investment advice."
          rows={4}
          disabled={loading || hasSubmitted}
          className="resize-none"
        />
        <p className="text-xs text-muted-foreground">
          Define the new expectation that this Sentinel will monitor.
        </p>
      </div>

      {/* Condition Field */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-gc-med text-foreground">
            Condition <span className="text-red-500">*</span>
          </Label>
        </div>
        <Textarea
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
          placeholder="The model response contains the word 'invest'."
          rows={4}
          disabled={loading || hasSubmitted}
          className="resize-none"
        />
        <p className="text-xs text-muted-foreground">
          Define the condition that will trigger this Sentinel.
        </p>
      </div>

      {/* Additional Questions Section */}
      {qaItems && qaItems.length > 0 && (
        <div className="space-y-6">
          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-gc-med text-foreground mb-4">
              Additional Questions
            </h3>
            <div className="space-y-6">
              {qaItems.map((item, index) => (
                <div key={index} className="space-y-3">
                  <Label className="text-sm font-gc-med text-foreground">
                    {item.question} <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    value={item.answer}
                    onChange={(e) => setQaItems((prev) => 
                      prev.map(q => 
                        q.question === item.question 
                          ? { ...q, answer: e.target.value }
                          : q
                      )
                    )}
                    rows={3}
                    disabled={loading}
                    className="resize-none"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <div>
          {qaItems && qaItems.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Please answer all questions to continue with sentinel generation.
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
            className="gap-2"
          >
            {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
            {loading 
              ? "Generating Sentinel..." 
              : qaItems && qaItems.length > 0 
                ? "Submit Answers" 
                : "Generate Sentinel"
            }
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewSentinelForm;
